/**
 * Agent Summary Card — War Room Phase C.2
 *
 * Displays one AI agent's overall position in the deliberation.
 * Shows confidence (color-coded), claim value, tools used, key finding,
 * and resolution paths count with total impact.
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Brain,
  Scale,
  Shield,
  Wrench,
  AlertTriangle,
  DollarSign,
} from 'lucide-react';
import { getAgentDisplayName, getMetricLabel, formatCurrency } from '@/services/deliberationApi';

// =============================================================================
// TYPES
// =============================================================================

export interface AgentSummaryCardProps {
  agentName: string;
  confidence: number | null;
  claimValue: string | null;
  toolsUsed: number;
  keyFinding: string;
  resolutionPathsCount: number;
  totalImpact: string | null;
  openItems?: number | null;   // curable blockers suppressing a claimant-side score
  projected?: string | null;   // projected score once blockers clear, e.g. "72-75"
}

// =============================================================================
// HELPERS
// =============================================================================

/** Get the icon for an agent role. */
function getAgentIcon(agentName: string) {
  if (agentName.includes('forensic')) return Brain;
  if (agentName.includes('attorney')) return Scale;
  if (agentName.includes('adjuster')) return Shield;
  return Brain;
}

/** Get confidence color based on threshold. */
function getConfidenceColor(confidence: number | null): React.CSSProperties {
  if (confidence === null) return { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
  if (confidence >= 0.8) return { backgroundColor: 'transparent', color: 'var(--color-verified)' };
  if (confidence >= 0.6) return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
  return { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' };
}

/** Render the score out of 100 (not a percent — it's a strength/integrity score). */
function getScoreLabel(confidence: number | null): string {
  if (confidence === null) return '—';
  return String(Math.round(confidence * 100));
}

/** Get border color for agent card. */
function getAgentBorderColor(agentName: string): React.CSSProperties {
  if (agentName.includes('forensic')) return { borderLeftColor: 'var(--color-brand)' };
  // Defense counsel is the adversarial carrier-side seat — must NOT read as your own
  // attorney. Checked before 'attorney' since "defendant_attorney" contains "attorney".
  if (agentName.includes('defendant')) return { borderLeftColor: 'var(--color-status-adverse)' };
  if (agentName.includes('attorney')) return { borderLeftColor: 'var(--color-brand)' };
  if (agentName.includes('adjuster')) return { borderLeftColor: 'var(--color-brand)' };
  return { borderLeftColor: 'var(--color-text-muted)' };
}

// =============================================================================
// COMPONENT
// =============================================================================

export function AgentSummaryCard({
  agentName,
  confidence,
  claimValue,
  toolsUsed,
  keyFinding,
  resolutionPathsCount,
  totalImpact,
  openItems,
  projected,
}: AgentSummaryCardProps) {
  const Icon = getAgentIcon(agentName);
  const displayName = getAgentDisplayName(agentName);
  const metricLabel = getMetricLabel(agentName);
  const borderColor = getAgentBorderColor(agentName);
  const confidenceColor = getConfidenceColor(confidence);

  return (
    <div className="rounded-xl shadow-lg border-l-4" style={{ backgroundColor: 'var(--color-bg-surface)', ...borderColor }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{displayName}</h3>
        </div>
        {/* Score chip: the metric named per seat (Case Strength / Claim Integrity / Carrier
            Position / Attack Strength), NOT a generic "confidence %" that reads as a grade. */}
        <Badge variant="outline" style={confidenceColor} title={`${metricLabel} score (0–100)`}>
          {metricLabel} {getScoreLabel(confidence)}
        </Badge>
      </div>

      {/* Body */}
      <div className="px-5 pb-4 space-y-3">
        {/* Readiness context: a claimant-side score is suppressed by curable open items —
            surface that so the number isn't read as a merits verdict. */}
        {openItems != null && openItems > 0 && (
          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>
              Held down by {openItems} open item{openItems !== 1 ? 's' : ''}
              {projected ? ` — rises to ${projected} once resolved` : ''}
            </span>
          </div>
        )}

        {/* Claim Value */}
        {claimValue && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            <span className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(claimValue)}</span>
          </div>
        )}

        {/* Key Finding */}
        <p className="text-sm line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{keyFinding}</p>

        {/* Stats Row */}
        <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <div className="flex items-center gap-1">
            <Wrench className="h-3 w-3" />
            <span>{toolsUsed} tools used</span>
          </div>
          {resolutionPathsCount > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              <span>{resolutionPathsCount} action{resolutionPathsCount !== 1 ? 's' : ''}</span>
            </div>
          )}
          {totalImpact && (
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <span>{formatCurrency(totalImpact)} impact</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentSummaryCard;
